import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Truck, Plane, Save, AlertTriangle, Info, ExternalLink, Shield, IndianRupee, Zap, BadgeCheck, Package, Clock } from 'lucide-react';
import VendorQuotesPanel from '../../components/shipping/VendorQuotesPanel';
import VendorHyperlocalCard from '../../components/shipping/VendorHyperlocalCard';
import { Badge } from '../../components/ui/badge';

const ZONE_LABELS = { A: 'Local', B: 'Regional', C: 'Metro', D: 'ROI', E: 'Remote' };
const ZONES = ['A', 'B', 'C', 'D', 'E'];

// ─── Shipping Origin Card ───────────────────────────────────────────────
const ShippingOriginCard = ({ origin }) => {
  if (!origin?.has_origin) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="shipping-origin-card">
        <CardContent className="flex items-center gap-3 py-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">No Shipping Origin Configured</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Add a primary location in Settings &gt; Locations to enable shipping calculations.
            </p>
          </div>
          <a href="/vendor/locations" className="text-xs text-amber-700 underline flex items-center gap-1">
            Add Location <ExternalLink className="h-3 w-3" />
          </a>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="shipping-origin-card">
      <CardContent className="flex items-center gap-3 py-4">
        <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
          <MapPin className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800" data-testid="origin-name">
            {origin.location_name}
          </p>
          <p className="text-xs text-slate-500 truncate" data-testid="origin-address">
            {[origin.address_line1, origin.city, origin.state, origin.postal_code].filter(Boolean).join(', ')}
          </p>
        </div>
        <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded" data-testid="origin-pincode">
          PIN: {origin.postal_code}
        </span>
        <a href="/vendor/locations" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          Change <ExternalLink className="h-3 w-3" />
        </a>
      </CardContent>
    </Card>
  );
};

// ─── iter-232.9 — Default Processing Time Card ──────────────────────────
// P0 — Vendor Default Processing Time (ETA Roadmap)
// Stored at users.vendor_profile.default_processing_time; used by the ETA
// endpoint when a listing has no per-listing override. Defaults to 1 Day.
const PROCESSING_PRESETS = [
  { id: '1d',  label: '1 Day',     min: 1, max: 1, unit: 'days' },
  { id: '2d',  label: '2 Days',    min: 2, max: 2, unit: 'days' },
  { id: '35d', label: '3–5 Days',  min: 3, max: 5, unit: 'days' },
  { id: '7d',  label: '7 Days',    min: 7, max: 7, unit: 'days' },
];

const matchPreset = (pt) => {
  if (!pt) return null;
  return PROCESSING_PRESETS.find(
    (p) => p.min === pt.min && p.max === pt.max && p.unit === pt.unit,
  )?.id || null;
};

const DefaultProcessingTimeCard = () => {
  const { apiCall } = useAuth();
  const [pt, setPt] = useState({ min: 1, max: 1, unit: 'days' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customMin, setCustomMin] = useState('');
  const [customMax, setCustomMax] = useState('');
  const [customUnit, setCustomUnit] = useState('days');
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall('/api/vendor/processing-time/default');
        const value = data?.default_processing_time || { min: 1, max: 1, unit: 'days' };
        setPt(value);
        if (!matchPreset(value)) {
          setShowCustom(true);
          setCustomMin(String(value.min));
          setCustomMax(String(value.max));
          setCustomUnit(value.unit);
        }
      } catch {
        // Silent fallback to system default (1 Day)
      } finally {
        setLoading(false);
      }
    })();
  }, [apiCall]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const res = await apiCall('/api/vendor/processing-time/default', {
        method: 'PUT',
        body: JSON.stringify(next),
      });
      setPt(res?.default_processing_time || next);
      toast.success('Default processing time saved');
    } catch (e) {
      toast.error('Could not save processing time');
    } finally {
      setSaving(false);
    }
  };

  const handlePreset = (preset) => {
    setShowCustom(false);
    persist({ min: preset.min, max: preset.max, unit: preset.unit });
  };

  const handleCustomSave = () => {
    const minNum = parseInt(customMin, 10);
    const maxNum = parseInt(customMax, 10);
    if (!Number.isFinite(minNum) || !Number.isFinite(maxNum) || minNum < 0 || maxNum < 0) {
      toast.error('Enter valid numbers');
      return;
    }
    if (minNum > maxNum) {
      toast.error('Min cannot be greater than max');
      return;
    }
    persist({ min: minNum, max: maxNum, unit: customUnit });
  };

  const currentPresetId = matchPreset(pt);

  if (loading) {
    return (
      <Card data-testid="default-processing-time-card">
        <CardContent className="py-4">
          <div className="animate-pulse h-12 bg-slate-100 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="default-processing-time-card">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
            <Clock className="h-4 w-4 text-indigo-700" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Default Processing Time</CardTitle>
            <CardDescription className="text-xs mt-1">
              How long you typically take to dispatch an order after it's placed. Used on Product Detail
              pages as <em>"Ships in&nbsp;…"</em> when a listing doesn't have its own override.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="processing-time-presets">
          {PROCESSING_PRESETS.map((preset) => {
            const active = currentPresetId === preset.id && !showCustom;
            return (
              <Button
                key={preset.id}
                size="sm"
                variant={active ? 'default' : 'outline'}
                disabled={saving}
                onClick={() => handlePreset(preset)}
                data-testid={`processing-time-preset-${preset.id}`}
              >
                {active ? '✓ ' : ''}{preset.label}
              </Button>
            );
          })}
          <Button
            size="sm"
            variant={showCustom || (!currentPresetId && pt) ? 'default' : 'outline'}
            disabled={saving}
            onClick={() => setShowCustom((v) => !v)}
            data-testid="processing-time-preset-custom"
          >
            Custom
          </Button>
        </div>

        {showCustom && (
          <div className="flex flex-wrap items-end gap-2 pt-1" data-testid="processing-time-custom-row">
            <div>
              <label className="block text-[11px] text-slate-600 mb-1">Min</label>
              <Input
                type="number"
                min="0"
                max="365"
                className="w-20"
                value={customMin}
                onChange={(e) => setCustomMin(e.target.value)}
                data-testid="processing-time-custom-min"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-600 mb-1">Max</label>
              <Input
                type="number"
                min="0"
                max="365"
                className="w-20"
                value={customMax}
                onChange={(e) => setCustomMax(e.target.value)}
                data-testid="processing-time-custom-max"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-600 mb-1">Unit</label>
              <select
                value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
                data-testid="processing-time-custom-unit"
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </div>
            <Button
              size="sm"
              onClick={handleCustomSave}
              disabled={saving}
              data-testid="processing-time-custom-save"
              className="gap-1.5"
            >
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}

        <div
          className="text-xs text-slate-600 bg-slate-50 border rounded-md px-3 py-2"
          data-testid="processing-time-current-summary"
        >
          <span className="text-slate-500">Current default:&nbsp;</span>
          <strong>
            {pt.min === pt.max
              ? `${pt.min} ${pt.unit === 'days' && pt.min === 1 ? 'Day' : pt.unit.replace(/s$/, pt.min === 1 ? '' : 's')}`
              : `${pt.min}–${pt.max} ${pt.unit}`}
          </strong>
          <span className="text-slate-400">
            {' '}· Listings can still override this individually.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
const RateMatrixTable = ({ mode, rates, caps, tierMeta, onChange, errors }) => {
  const isPerKg = (tier) => tierMeta?.[tier]?.rate_type === 'per_kg';
  const isQuoteRequired = (tier, zone) => {
    const capVal = caps?.[tier]?.[zone];
    return capVal === null || capVal === undefined;
  };

  const getCellError = (tier, zone) => {
    return errors.find((e) => String(e.tier) === tier && e.zone === zone && e.mode === mode);
  };

  return (
    <div className="overflow-x-auto" data-testid={`rate-matrix-${mode}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b">
            <th className="text-left py-2.5 px-3 font-semibold text-slate-700 min-w-[150px]">Tier</th>
            <th className="text-left py-2.5 px-2 font-medium text-slate-500 text-xs min-w-[100px]">Weight</th>
            {ZONES.map((z) => (
              <th key={z} className="text-center py-2.5 px-2 font-medium text-slate-600 min-w-[95px]">
                <span className="block text-xs font-semibold">{z}</span>
                <span className="block text-[10px] text-slate-400">{ZONE_LABELS[z]}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5, 6].map((tierNum) => {
            const t = String(tierNum);
            const meta = tierMeta?.[t] || {};
            const perKg = isPerKg(t);
            const isSpecialized = tierNum === 6;

            return (
              <tr
                key={tierNum}
                className={`border-b last:border-b-0 ${isSpecialized ? 'bg-slate-50/50' : 'hover:bg-slate-25'}`}
                data-testid={`tier-row-${tierNum}`}
              >
                <td className="py-2 px-3">
                  <span className="font-medium text-slate-800">{meta.name || `Tier ${tierNum}`}</span>
                  {perKg && <span className="ml-1.5 text-[10px] text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">/kg</span>}
                </td>
                <td className="py-2 px-2 text-xs text-slate-500">{meta.weight_range || ''}</td>
                {ZONES.map((zone) => {
                  const quoteReq = isQuoteRequired(t, zone);
                  const capVal = caps?.[t]?.[zone];
                  const cellErr = getCellError(t, zone);
                  const val = rates?.[t]?.[zone];

                  // Specialized tier: show "Included in Price" label
                  if (isSpecialized && quoteReq) {
                    return (
                      <td key={zone} className="py-2 px-2 text-center">
                        <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded font-medium">
                          Quote Req.
                        </span>
                      </td>
                    );
                  }

                  return (
                    <td key={zone} className="py-2 px-1">
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={val ?? ''}
                          data-testid={`rate-input-${mode}-${tierNum}-${zone}`}
                          onChange={(e) => onChange(t, zone, e.target.value === '' ? 0 : Number(e.target.value))}
                          className={`h-8 text-center text-xs font-mono w-full ${
                            cellErr
                              ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-400'
                              : 'border-slate-200'
                          }`}
                        />
                        {capVal != null && (
                          <span className="absolute -bottom-3.5 left-0 right-0 text-center text-[9px] text-slate-400">
                            max {capVal}
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Specialized Tier Info Banner */}
      <div className="mt-4 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
        <Info className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
        <div className="text-xs text-emerald-700">
          <strong>Tier 6 — Specialized:</strong> Items over{' '}
          {mode === 'surface' ? '50kg' : '25kg'} or priced above ₹50,000 —
          shipping is <strong>Included in Price</strong>. The buyer sees Free Shipping and
          you must factor delivery cost into your listed price.
        </div>
      </div>
    </div>
  );
};

// ─── Customer Rate Chart (Optical Fee) ──────────────────────────────────
const CustomerRateChart = ({ apiCall }) => {
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [rateView, setRateView] = useState('surface');

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall('/api/shipping/customer-rate-chart');
        setChartData(data);
      } catch {
        toast.error('Failed to load customer rate chart');
      } finally {
        setChartLoading(false);
      }
    })();
  }, [apiCall]);

  if (chartLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin h-6 w-6 border-4 border-slate-300 border-t-slate-700 rounded-full" />
      </div>
    );
  }

  if (!chartData) return null;

  const { zones, business_rules: rules } = chartData;
  const matrix = rateView === 'surface' ? chartData.surface : chartData.air;
  const zoneKeys = Object.keys(zones);

  return (
    <div className="space-y-5" data-testid="customer-rate-chart-section">
      {/* Intro */}
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardContent className="py-4 px-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
              <IndianRupee className="h-4 w-4 text-indigo-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-indigo-900">What Customers Pay for Shipping</p>
              <p className="text-xs text-indigo-700 mt-1 leading-relaxed">
                The table below shows the <strong>subsidised shipping fee</strong> that Aniyor charges customers at checkout.
                These rates are significantly lower than actual courier costs — the gap is covered by the <strong>5% Vendor Shipping Pool</strong> and
                Aniyor's <strong>3% Platform Reserve</strong> (from your commission). If costs exceed both pools, a small surcharge may apply to marked tiers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Rules */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border rounded-lg p-3.5 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1.5">
            <BadgeCheck className="h-4 w-4 text-green-600" />
            <p className="text-xs font-bold text-green-800">Free Shipping</p>
          </div>
          <p className="text-[11px] text-green-700 leading-relaxed">
            Cart above <strong>₹{rules.free_shipping_cart_threshold.toLocaleString('en-IN')}</strong> in Zones A–D gets free shipping.
            The waterfall still settles internally.
          </p>
        </div>
        <div className="border rounded-lg p-3.5 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <p className="text-xs font-bold text-amber-800">No Subsidy</p>
          </div>
          <p className="text-[11px] text-amber-700 leading-relaxed">
            Orders below <strong>₹{rules.no_subsidy_threshold}</strong> — customer pays the full courier rate, no subsidy applied.
          </p>
        </div>
        <div className="border rounded-lg p-3.5 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1.5">
            <Zap className="h-4 w-4 text-purple-600" />
            <p className="text-xs font-bold text-purple-800">High-Value Items</p>
          </div>
          <p className="text-[11px] text-purple-700 leading-relaxed">
            Products above <strong>₹{rules.high_value_threshold.toLocaleString('en-IN')}</strong> — shipping is ₹0 ("Included in Price"). Factor cost into your listing price.
          </p>
        </div>
        <div className="border rounded-lg p-3.5 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1.5">
            <IndianRupee className="h-4 w-4 text-blue-600" />
            <p className="text-xs font-bold text-blue-800">5% Vendor Pool</p>
          </div>
          <p className="text-[11px] text-blue-700 leading-relaxed">
            {rules.vendor_bump_percent}% of your base price is bumped and pooled to cover the difference between customer rate and real courier cost.
          </p>
        </div>
      </div>

      {/* Rate Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Subsidised Rate Chart</CardTitle>
              <CardDescription className="mt-0.5">
                All rates in ₹ (INR). Tiers 5–6 are <strong>per kg</strong> — final charge = rate × weight.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={rateView} onValueChange={setRateView}>
            <TabsList className="mb-4" data-testid="customer-rate-mode-tabs">
              <TabsTrigger value="surface" className="gap-1.5" data-testid="customer-tab-surface">
                <Truck className="h-3.5 w-3.5" /> Surface
              </TabsTrigger>
              <TabsTrigger value="air" className="gap-1.5" data-testid="customer-tab-air">
                <Plane className="h-3.5 w-3.5" /> Air
              </TabsTrigger>
            </TabsList>

            <div className="overflow-x-auto" data-testid={`customer-rate-table-${rateView}`}>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left py-2.5 px-3 font-semibold text-slate-700 min-w-[130px]">Tier</th>
                    <th className="text-left py-2.5 px-2 font-medium text-slate-500 text-xs min-w-[90px]">Weight</th>
                    <th className="text-center py-2.5 px-2 font-medium text-slate-500 text-xs min-w-[50px]">Type</th>
                    {zoneKeys.map((z) => (
                      <th key={z} className="text-center py-2.5 px-2 min-w-[80px]">
                        <span className="font-semibold text-slate-700">Zone {z}</span>
                        <br />
                        <span className="text-[10px] text-slate-400 font-normal">{zones[z]}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row) => (
                    <tr key={row.tier} className="border-b last:border-b-0 hover:bg-slate-50/50">
                      <td className="py-2.5 px-3">
                        <span className="font-semibold text-slate-800">{row.name}</span>
                      </td>
                      <td className="py-2.5 px-2 text-xs text-slate-500">{row.weight_range}</td>
                      <td className="py-2.5 px-2 text-center">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${row.rate_type === 'per_kg' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-slate-50 text-slate-600'}`}>
                          {row.rate_type === 'per_kg' ? '/kg' : 'flat'}
                        </Badge>
                      </td>
                      {zoneKeys.map((z) => {
                        const rate = row.rates[z];
                        const hasSurcharge = row.surcharge[z];
                        const isQuote = rate === null || rate === undefined;
                        return (
                          <td key={z} className="py-2.5 px-2 text-center">
                            {isQuote ? (
                              <span className="text-xs text-slate-400 italic">Quote</span>
                            ) : (
                              <div className="flex items-center justify-center gap-0.5">
                                <span className={`font-mono font-semibold text-sm ${hasSurcharge ? 'text-amber-700' : 'text-slate-800'}`}>
                                  ₹{rate}
                                </span>
                                {hasSurcharge && (
                                  <span className="text-amber-500 text-[10px]" title="Surcharge may apply if waterfall is exhausted">*</span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tabs>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-slate-500 px-1">
            <span className="flex items-center gap-1">
              <span className="font-mono font-semibold text-slate-800">₹30</span> = Customer pays ₹30
            </span>
            <span className="flex items-center gap-1">
              <span className="font-mono font-semibold text-amber-700">₹150</span><span className="text-amber-500">*</span> = Surcharge may apply if the 5% pool + 3% reserve don't cover the courier cost
            </span>
            <span className="flex items-center gap-1">
              <span className="italic text-slate-400">Quote</span> = Contact Aniyor for custom pricing
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Waterfall Explanation */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">How the 5% Shipping Pool Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-xs text-slate-600 leading-relaxed">
              When a customer places an order, the actual courier cost (from Delhivery) is settled using a <strong>waterfall</strong> system.
              This means multiple pools absorb the cost step-by-step, so the customer always sees the subsidised rate above.
            </p>
            <div className="flex flex-col gap-0">
              {[
                { step: '1', label: 'Vendor 5% Bump Pool', color: 'bg-blue-500', desc: 'Your 5% shipping contribution (built into the bumped product price) absorbs the courier cost first. Capped at Zone E max rate.' },
                { step: '2', label: 'Aniyor 3% Reserve', color: 'bg-indigo-500', desc: 'If the 5% pool isn\'t enough, up to 3% of the platform commission is used to cover the remaining cost.' },
                { step: '3', label: 'Customer Optical Fee', color: 'bg-emerald-500', desc: 'The subsidised amount shown in the table above — this is what the customer actually pays at checkout.' },
                { step: '4', label: 'Surcharge (if needed)', color: 'bg-amber-500', desc: 'For tiers marked with *, if pools 1+2 are exhausted and the optical fee doesn\'t cover the full cost, a small surcharge is added.' },
              ].map(({ step, label, color, desc }) => (
                <div key={step} className="flex items-start gap-3 py-2.5 border-b last:border-b-0">
                  <div className={`h-6 w-6 rounded-full ${color} text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5`}>
                    {step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 border rounded-md p-3 mt-2">
              <p className="text-xs text-slate-600">
                <strong>For Self-Managed Shipping:</strong> If your actual courier cost is higher than the subsidised rate above,
                you are responsible for the difference. You can either absorb it or factor the extra cost into your product price.
                Aniyor will continue to charge the customer the subsidised rate shown above.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────
const VendorShipping = () => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [origin, setOrigin] = useState(null);
  const [surfaceRates, setSurfaceRates] = useState({});
  const [airRates, setAirRates] = useState({});
  const [caps, setCaps] = useState({ surface: {}, air: {} });
  const [tierMeta, setTierMeta] = useState({ surface: {}, air: {} });
  const [validationErrors, setValidationErrors] = useState([]);
  const [activeTab, setActiveTab] = useState('surface');
  const [shippingMode, setShippingMode] = useState('managed');
  const [modeConfirmed, setModeConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [originRes, matrixRes, capsRes, settingsRes] = await Promise.all([
        apiCall('/api/shipping-settings/vendor/origin'),
        apiCall('/api/shipping-settings/vendor/matrix'),
        apiCall('/api/shipping-settings/platform/caps'),
        apiCall('/api/shipping-settings/vendor').catch(() => null),
      ]);
      setOrigin(originRes);
      setSurfaceRates(matrixRes.surface || {});
      setAirRates(matrixRes.air || {});
      setCaps({ surface: capsRes.surface?.caps || {}, air: capsRes.air?.caps || {} });
      setTierMeta({ surface: capsRes.surface?.tier_meta || {}, air: capsRes.air?.tier_meta || {} });
      // Initialize shipping mode state from backend
      if (settingsRes) {
        const backendMode = settingsRes.shipping_mode;
        if (backendMode === 'self_managed') setShippingMode('self');
        else if (backendMode === 'aniyor_managed') setShippingMode('managed');
        setModeConfirmed(!!settingsRes.shipping_mode_confirmed);
      }
    } catch (err) {
      toast.error('Failed to load shipping data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRateChange = (mode, tier, zone, value) => {
    const setter = mode === 'surface' ? setSurfaceRates : setAirRates;
    setter((prev) => ({
      ...prev,
      [tier]: { ...(prev[tier] || {}), [zone]: value },
    }));
    // Clear error for this cell
    setValidationErrors((prev) =>
      prev.filter((e) => !(String(e.tier) === tier && e.zone === zone && e.mode === mode))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setValidationErrors([]);
    try {
      // Use direct fetch to get structured error detail for validation
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipping-settings/vendor/matrix`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ surface: surfaceRates, air: airRates }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail;
        if (detail && typeof detail === 'object' && detail.errors) {
          setValidationErrors(detail.errors);
          toast.error(`${detail.errors.length} rate(s) exceed platform caps — highlighted in red`);
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Failed to save shipping rates');
        }
        return;
      }
      toast.success('Shipping rates saved successfully');
    } catch (err) {
      toast.error('Failed to save shipping rates');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-700 rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-6xl mx-auto" data-testid="vendor-shipping-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Shipping & Delivery</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Configure your shipping rates by weight tier and delivery zone
            </p>
          </div>
          {shippingMode === 'self' && (
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-shipping-rates-btn"
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Rates'}
            </Button>
          )}
        </div>

        {/* Shipping Origin */}
        <ShippingOriginCard origin={origin} />

        {/* iter-232.9 — Default Processing Time (P0) */}
        <DefaultProcessingTimeCard />

        {/* Shipping Mode Confirmation Card (drives product-level defaults + invoice split) */}
        <Card className={modeConfirmed ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/40'} data-testid="shipping-mode-confirm-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4 text-indigo-600" />
                  Shipping Mode
                  {modeConfirmed && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                      ✓ Currently: {shippingMode === 'self' ? 'Self-Managed' : 'Aniyor-Managed'}
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  This is the platform default applied to your listings. You can switch modes anytime.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-600">
              <strong>Aniyor-Managed:</strong> platform handles pickup, delivery, and generates a separate Marketplace invoice with 18% GST for shipping.
              <span className="mx-2 text-gray-400">|</span>
              <strong>Self-Managed:</strong> you arrange your own delivery; shipping appears as a line item in your invoice to the customer.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                variant={shippingMode === 'managed' && modeConfirmed ? 'default' : 'outline'}
                disabled={confirming}
                onClick={async () => {
                  setConfirming(true);
                  try {
                    await apiCall('/api/shipping-settings/vendor', {
                      method: 'POST',
                      body: JSON.stringify({
                        shipping_mode: 'aniyor_managed',
                        shipping_mode_confirmed: true,
                      }),
                    });
                    setShippingMode('managed');
                    setModeConfirmed(true);
                    toast.success('Switched to Aniyor-Managed shipping');
                  } catch (e) {
                    toast.error('Could not save — please try again');
                  } finally {
                    setConfirming(false);
                  }
                }}
                data-testid="confirm-aniyor-managed-btn"
              >
                {shippingMode === 'managed' && modeConfirmed ? '✓ Aniyor-Managed (Active)' : 'Use Aniyor-Managed'}
              </Button>
              <Button
                size="sm"
                variant={shippingMode === 'self' && modeConfirmed ? 'default' : 'outline'}
                disabled={confirming}
                onClick={async () => {
                  setConfirming(true);
                  try {
                    await apiCall('/api/shipping-settings/vendor', {
                      method: 'POST',
                      body: JSON.stringify({
                        shipping_mode: 'self_managed',
                        shipping_mode_confirmed: true,
                      }),
                    });
                    setShippingMode('self');
                    setModeConfirmed(true);
                    toast.success('Switched to Self-Managed shipping');
                  } catch (e) {
                    toast.error('Could not save — please try again');
                  } finally {
                    setConfirming(false);
                  }
                }}
                data-testid="confirm-self-managed-btn"
              >
                {shippingMode === 'self' && modeConfirmed ? '✓ Self-Managed (Active)' : 'Switch to Self-Managed'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Top-level Aniyor Managed / Self Managed tabs */}
        <Tabs value={shippingMode} onValueChange={setShippingMode}>
          <TabsList className="mb-4" data-testid="shipping-type-tabs">
            <TabsTrigger value="managed" data-testid="tab-aniyor-managed">
              Aniyor Managed
            </TabsTrigger>
            <TabsTrigger value="self" data-testid="tab-self-managed">
              Self Managed
            </TabsTrigger>
            <TabsTrigger value="customer-rates" data-testid="tab-customer-rates">
              <IndianRupee className="h-3.5 w-3.5 mr-1" />
              Customer Shipping Rates
            </TabsTrigger>
          </TabsList>

          {/* ── Aniyor Managed Tab ── */}
          <TabsContent value="managed" className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How Aniyor Managed Shipping Works</CardTitle>
                <CardDescription>
                  Aniyor handles the logistics via our courier network (Delhivery). Your products are automatically priced with a 5% shipping pool contribution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4 space-y-1">
                    <p className="text-sm font-semibold text-slate-700">5% Shipping Pool</p>
                    <p className="text-xs text-slate-500">
                      A small % of your base price is pooled to subsidize delivery costs for customers.
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 space-y-1">
                    <p className="text-sm font-semibold text-slate-700">Waterfall Settlement</p>
                    <p className="text-xs text-slate-500">
                      Real courier cost is covered by: your pool → Aniyor reserve → customer optical fee → surcharge.
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 space-y-1">
                    <p className="text-sm font-semibold text-slate-700">Free Shipping Rules</p>
                    <p className="text-xs text-slate-500">
                      Orders above ₹2,000 in Zones A-D get free shipping automatically. High-value items (₹50K+) always ship free.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
                  <p className="text-xs text-blue-700">
                    <strong>Tip:</strong> Your listing price on Aniyor = Base Price + 5% Shipping Contribution.
                    You can see the exact breakdown in the Pricing Card on each listing.
                    {' '}If MRP is a concern, use the &quot;Absorb Shipping&quot; toggle on the listing form.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Hyperlocal Delivery Settings */}
            <VendorHyperlocalCard />

            {/* Quote Requests */}
            <VendorQuotesPanel />
          </TabsContent>

          {/* ── Self Managed Tab ── */}
          <TabsContent value="self" className="space-y-5">
            {/* Rate Matrix */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Shipping Rate Matrix</CardTitle>
                <CardDescription>
                  Set your rates per tier and zone. Rates exceeding platform caps (DTDC benchmark) will be highlighted.
                  Tiers 5+ use <strong>per-kg</strong> pricing — final cost = rate &times; item weight.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-4" data-testid="shipping-mode-tabs">
                    <TabsTrigger value="surface" className="gap-1.5" data-testid="tab-surface">
                      <Truck className="h-3.5 w-3.5" /> Surface
                    </TabsTrigger>
                    <TabsTrigger value="air" className="gap-1.5" data-testid="tab-air">
                      <Plane className="h-3.5 w-3.5" /> Air
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="surface">
                    <RateMatrixTable
                      mode="surface"
                      rates={surfaceRates}
                      caps={caps.surface}
                      tierMeta={tierMeta.surface}
                      onChange={(tier, zone, val) => handleRateChange('surface', tier, zone, val)}
                      errors={validationErrors}
                    />
                  </TabsContent>

                  <TabsContent value="air">
                    <RateMatrixTable
                      mode="air"
                      rates={airRates}
                      caps={caps.air}
                      tierMeta={tierMeta.air}
                      onChange={(tier, zone, val) => handleRateChange('air', tier, zone, val)}
                      errors={validationErrors}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Disclaimer Card */}
            <Card className="border-slate-200 bg-slate-50" data-testid="shipping-disclaimer-card">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
                  <div className="text-xs text-slate-600 space-y-2">
                    <p className="font-semibold text-slate-700 text-sm">Merchant-Managed Shipping Agreement</p>
                    <ul className="space-y-1.5 list-none">
                      <li>
                        <strong>Market Benchmarking:</strong> Shipping rates are capped at DTDC benchmark levels
                        (inclusive of 30% fuel surcharge + 18% GST). Rates exceeding this will be automatically
                        adjusted at checkout.
                      </li>
                      <li>
                        <strong>Direct Responsibility:</strong> For all self-managed shipments, you bear 100%
                        responsibility for the item until the buyer confirms receipt.
                      </li>
                      <li>
                        <strong>Platform Indemnity:</strong> Aniyor Marketplace is not liable for any loss, theft,
                        or damage occurring during transit for merchant-fulfilled orders.
                      </li>
                      <li>
                        <strong>Insurance Mandate:</strong> For items valued above ₹50,000, you are strongly
                        advised to purchase third-party transit insurance. The cost of insurance should be factored
                        into the product price.
                      </li>
                      <li>
                        <strong>Proof of Delivery:</strong> For high-value items, you must upload a signed POD or
                        delivery photo to the order dashboard to trigger the payout cycle.
                      </li>
                      <li>
                        <strong>Remote Deliveries:</strong> Shipments to Zone E (J&K, North East, Islands) may
                        incur surcharges. Verify serviceability before shipping.
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Customer Shipping Rates Tab ── */}
          <TabsContent value="customer-rates">
            <CustomerRateChart apiCall={apiCall} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default VendorShipping;
